import React, { useState, useEffect } from 'react';
import '../styles/GuidedTour.css';

const TOUR_STEPS = [
  {
    target: '[data-tour="strategy-room"]',
    title: 'Welcome to TheBox! 🎮',
    content: 'This is your Strategy Room. Here you can create visual playbooks by drawing routes, placing utilities, and organizing your team\'s strategies.',
    position: 'bottom',
  },
  {
    target: '[data-tour="playbook"]',
    title: 'Your Playbook Library',
    content: 'All your saved strategies are organized here. Filter by map, mode, or search for specific plays.',
    position: 'bottom',
  },
  {
    target: '[data-tour="calendar"]',
    title: 'Team Calendar',
    content: 'Schedule scrims, reviews, and match days. Keep your team aligned on upcoming events.',
    position: 'bottom',
  },
  {
    target: '[data-tour="stats"]',
    title: 'Stats & Analytics',
    content: 'Track your team\'s performance and scout opponents. Data-driven decisions start here.',
    position: 'bottom',
  },
  {
    target: '[data-tour="settings"]',
    title: 'Settings',
    content: 'Manage your profile, team settings, and preferences. You can change your role here too!',
    position: 'left',
  },
];

const GuidedTour = ({ isActive, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetElement, setTargetElement] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });

  useEffect(() => {
    if (!isActive || currentStep >= TOUR_STEPS.length) {
      if (isActive && currentStep >= TOUR_STEPS.length) {
        onComplete();
      }
      return;
    }

    const step = TOUR_STEPS[currentStep];
    const element = document.querySelector(step.target);

    if (element) {
      setTargetElement(element);
      updatePosition(element);
      
      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Update position on scroll/resize
      const updateOnResize = () => updatePosition(element);
      window.addEventListener('scroll', updateOnResize, true);
      window.addEventListener('resize', updateOnResize);

      return () => {
        window.removeEventListener('scroll', updateOnResize, true);
        window.removeEventListener('resize', updateOnResize);
      };
    } else {
      // Element not found, skip to next step
      setTimeout(() => {
        if (currentStep < TOUR_STEPS.length - 1) {
          setCurrentStep(currentStep + 1);
        } else {
          onComplete();
        }
      }, 500);
    }
  }, [isActive, currentStep, onComplete]);

  const updatePosition = (element) => {
    if (!element) return;
    const rect = element.getBoundingClientRect();
    setPosition({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      height: rect.height,
    });
  };

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  if (!isActive || currentStep >= TOUR_STEPS.length || !targetElement) {
    return null;
  }

  const step = TOUR_STEPS[currentStep];
  const stepNum = currentStep + 1;
  const totalSteps = TOUR_STEPS.length;

  // Calculate tooltip position based on step position preference
  let tooltipStyle = {};
  const spacing = 20;
  
  switch (step.position) {
    case 'bottom':
      tooltipStyle = {
        top: position.top + position.height + spacing,
        left: position.left + position.width / 2,
        transform: 'translateX(-50%)',
      };
      break;
    case 'top':
      tooltipStyle = {
        bottom: window.innerHeight - position.top + spacing,
        left: position.left + position.width / 2,
        transform: 'translateX(-50%)',
      };
      break;
    case 'left':
      tooltipStyle = {
        top: position.top + position.height / 2,
        right: window.innerWidth - position.left + spacing,
        transform: 'translateY(-50%)',
      };
      break;
    case 'right':
      tooltipStyle = {
        top: position.top + position.height / 2,
        left: position.left + position.width + spacing,
        transform: 'translateY(-50%)',
      };
      break;
    default:
      tooltipStyle = {
        top: position.top + position.height + spacing,
        left: position.left + position.width / 2,
        transform: 'translateX(-50%)',
      };
  }

  return (
    <>
      {/* Overlay */}
      <div className="tour-overlay" onClick={handleSkip} />
      
      {/* Highlight */}
      <div
        className="tour-highlight"
        style={{
          top: position.top - 4,
          left: position.left - 4,
          width: position.width + 8,
          height: position.height + 8,
        }}
      />
      
      {/* Tooltip */}
      <div className="tour-tooltip" style={tooltipStyle}>
        <div className="tour-tooltip-header">
          <div>
            <h3 className="tour-tooltip-title">{step.title}</h3>
            <p className="tour-tooltip-step">{stepNum} of {totalSteps}</p>
          </div>
          <button className="tour-close" onClick={handleSkip}>×</button>
        </div>
        <p className="tour-tooltip-content">{step.content}</p>
        <div className="tour-tooltip-footer">
          <button className="tour-btn-skip" onClick={handleSkip}>
            Skip tour
          </button>
          <div className="tour-btn-group">
            {currentStep > 0 && (
              <button className="tour-btn-secondary" onClick={handlePrev}>
                Back
              </button>
            )}
            <button className="tour-btn-primary" onClick={handleNext}>
              {currentStep === totalSteps - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default GuidedTour;

